import { FC, useEffect, useMemo, useState } from 'react'
import { Button, Modal, Space, Typography, message } from 'antd'
import { CalendarOutlined, CloseOutlined } from '@ant-design/icons'
import { useAppSelector } from 'store'
import {
  useTrainingAnnouncements,
  useRegisterTrainingRound,
  upcomingRound,
  hasUpcomingRound,
} from 'hooks/useTraining'
import { isEmailRequiredError } from 'services/eventCalendarApi'
import { TrainingEvent } from 'services/interface/eventCalendar/eventCalendar.interface'
import { thaiDate } from 'utils/dateFormat'
import NeedEmailModal, { useRequireEmail } from './NeedEmailModal'
import TrainingDetailDrawer from './TrainingDetailDrawer'

const { Text } = Typography

const DISMISS_KEY = 'training_marquee_hidden'
/** สลับหลักสูตรถัดไปทุกกี่มิลลิวินาที */
const ROTATE_MS = 8000

/**
 * ปิดป้าย = ปิดทั้งแถบ (ทุกหลักสูตร) แต่แค่รอบการใช้งานนี้ — sessionStorage หายเองเมื่อ
 * ปิดเบราว์เซอร์ ป้ายจึงกลับมาเชิญใหม่ครั้งหน้า ไม่ต้องมีวันหมดอายุให้คำนวณเอง
 */
const readHidden = (): boolean => {
  try {
    return sessionStorage.getItem(DISMISS_KEY) === '1'
  } catch {
    return false
  }
}

const writeHidden = () => {
  try {
    sessionStorage.setItem(DISMISS_KEY, '1')
  } catch {
    // storage ใช้ไม่ได้ (private mode) → ปิดได้แค่จนกว่าจะรีเฟรช ไม่ใช่เรื่องคอขวด
  }
}

/** ป้ายเชิญเข้าร่วมอบรม — วางใต้ header เหนือ content */
const TrainingMarquee: FC = () => {
  const profile = useAppSelector(state => state.auth.profile)
  const isFactory = profile?.user?.role?.ref_type === 'factory'

  const { data } = useTrainingAnnouncements(isFactory)
  const registerMutation = useRegisterTrainingRound()
  const { ensureEmail, needEmailOpen, openNeedEmail, closeNeedEmail } = useRequireEmail()

  const [hidden, setHidden] = useState(false)
  const [index, setIndex] = useState(0)
  const [detailEventId, setDetailEventId] = useState<string | undefined>()

  useEffect(() => {
    setHidden(readHidden())
  }, [])

  /** เอาเฉพาะหลักสูตรที่ยังมีรอบข้างหน้าและยังไม่ได้จอง */
  const events: TrainingEvent[] = useMemo(() => {
    const list = data?.data?.data ?? []

    return list.filter(event => !event.is_registered && hasUpcomingRound(event.rounds_summary))
  }, [data])

  // วนสลับหลักสูตร (ทำเฉพาะเมื่อมีมากกว่า 1 อัน)
  useEffect(() => {
    if (events.length <= 1) return
    const timer = setInterval(() => setIndex(prev => (prev + 1) % events.length), ROTATE_MS)

    return () => clearInterval(timer)
    // index อยู่ใน deps เพื่อให้กดจุดข้ามใบแล้วเริ่มจับเวลาใหม่ (ไม่ใช่เปลี่ยนต่ออีกวินาทีเดียว)
  }, [events.length, index])

  useEffect(() => {
    if (index >= events.length) setIndex(0)
  }, [events.length, index])

  const current = events[index]

  const handleJoin = () => {
    if (!current) return

    const summary = current.rounds_summary
    const nextRound = upcomingRound(summary)
    // มีรอบให้เลือกหลายรอบ → ต้องเปิดรายละเอียดให้เลือกรอบเอง
    if (!nextRound || (summary?.round_count ?? 0) > 1) {
      setDetailEventId(current.id)

      return
    }
    if (!ensureEmail()) return

    const round = nextRound
    Modal.confirm({
      title: 'เข้าร่วมอบรม',
      content: `ยืนยันจองรอบ ${round.round_date} เวลา ${round.start_time}${
        round.end_time ? ` - ${round.end_time}` : ''
      } ของหลักสูตร "${current.name}"?`,
      okText: 'จองเลย',
      cancelText: 'ยกเลิก',
      onOk: async () => {
        try {
          await registerMutation.mutateAsync({ event_id: current.id, round_id: round.id })
          message.success('จองรอบอบรมเรียบร้อยแล้ว ระบบจะส่งรายละเอียดไปที่อีเมลของคุณ')
        } catch (error: any) {
          if (isEmailRequiredError(error)) {
            openNeedEmail()

            return
          }
          message.error(
            error?.response?.data?.description || error?.response?.data?.message || 'จองรอบไม่สำเร็จ'
          )
        }
      },
    })
  }

  const handleDismiss = () => {
    writeHidden()
    setHidden(true)
  }

  if (!isFactory || hidden || !current) return null

  const round = upcomingRound(current.rounds_summary)
  const dateText = round
    ? `${thaiDate(round.round_date)} เวลา ${round.start_time}${
        round.end_time ? `-${round.end_time}` : ''
      }`
    : ''

  return (
    <>
      {/* สลับหลักสูตร = ข้อความสไลด์ขึ้นแล้วจางเข้า (key ที่ id ทำให้ React เล่นซ้ำทุกครั้งที่เปลี่ยน) */}
      <style>{`
        @keyframes bb-training-swap {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .bb-training-marquee__text {
          animation: bb-training-swap 400ms ease-out;
        }
        .bb-training-marquee__dot {
          width: 6px;
          height: 6px;
          padding: 0;
          border: 0;
          border-radius: 50%;
          background: #91caff;
          cursor: pointer;
          transition: background 200ms, width 200ms;
        }
        .bb-training-marquee__dot[aria-current='true'] {
          width: 14px;
          border-radius: 3px;
          background: #0958d9;
        }
        @media (prefers-reduced-motion: reduce) {
          .bb-training-marquee__text {
            animation: none;
          }
        }
      `}</style>

      <div
        className="bb-training-marquee"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          background: 'linear-gradient(90deg, #e6f4ff 0%, #f0f7ff 100%)',
          borderBottom: '1px solid #bae0ff',
          padding: '8px 16px',
          margin: '0 20px 16px',
          minHeight: 44,
          overflow: 'hidden',
        }}
      >
        <CalendarOutlined style={{ color: '#2777c8', flexShrink: 0, fontSize: 18 }} />

        <div key={current.id} className="bb-training-marquee__text" style={{ flex: 1, overflow: 'hidden', minWidth: 0 }}>
          <Text style={{ color: '#0958d9' }} ellipsis>
            ระบบมีการจัดอบรม <strong>&ldquo;{current.name}&rdquo;</strong>
            {dateText ? ` ${dateText}` : ''} — คุณต้องการเข้าร่วมไหม?
          </Text>
        </div>

        {/* จุดบอกว่ามีหลายหลักสูตร — กดข้ามไปใบที่ต้องการได้เลย ไม่ต้องรอวน */}
        {events.length > 1 && (
          <Space size={4} style={{ flexShrink: 0 }}>
            {events.map((event, dotIndex) => (
              <button
                key={event.id}
                type="button"
                className="bb-training-marquee__dot"
                aria-current={dotIndex === index}
                aria-label={event.name}
                title={event.name}
                onClick={() => setIndex(dotIndex)}
              />
            ))}
          </Space>
        )}

        <Space size={4} style={{ flexShrink: 0 }}>
          <Button type="primary" size="small" onClick={handleJoin}>
            เข้าร่วม
          </Button>
          <Button size="small" onClick={() => setDetailEventId(current.id)}>
            ดูเพิ่มเติม
          </Button>
          <Button
            type="text"
            size="small"
            icon={<CloseOutlined />}
            onClick={handleDismiss}
            title="ปิดแถบนี้ (กลับมาแสดงครั้งหน้าที่เปิดเบราว์เซอร์)"
          />
        </Space>
      </div>

      <TrainingDetailDrawer
        eventId={detailEventId}
        open={!!detailEventId}
        onClose={() => setDetailEventId(undefined)}
      />

      <NeedEmailModal open={needEmailOpen} onClose={closeNeedEmail} />
    </>
  )
}

export default TrainingMarquee
